/**
 * A small persistent TTL cache (feature #4).
 *
 * The Drive library is STATIC — the catalog of artists and genres does not
 * change between visits (see the project's static-library note) — so re-fetching
 * the artist and genre lists on every navigation is pure waste. This caches
 * their responses under a time-to-live (24h by default) so what was loaded once
 * is served instantly until it expires, across reloads.
 *
 * Backed by `localStorage` (survives reloads) with an in-memory fallback for
 * the session when storage is blocked or full. Every access is guarded: a
 * corrupt entry, a quota rejection, or an absent API degrades to "cache miss",
 * never a throw into the data path. Oversized values are simply not persisted —
 * a cache that cannot store a giant list still lets the fetch succeed.
 *
 * @module
 */

/** The `localStorage` surface used — injectable for tests. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface TtlCache {
  /** The cached value if present AND unexpired; otherwise null. */
  get<T>(key: string): T | null;
  /** Store a value under the cache's TTL. Silent on failure. */
  set(key: string, value: unknown): void;
  /** Drop one key. */
  delete(key: string): void;
}

export interface TtlCacheDeps {
  readonly storage?: StorageLike | null;
  /** Cache lifetime in ms. Default 24h. */
  readonly ttlMs?: number;
  /** Key prefix, so several caches can share one storage. */
  readonly namespace?: string;
  /** Clock, injectable for tests. */
  readonly now?: () => number;
  /** Values whose JSON exceeds this are not persisted (default ~1 MiB). */
  readonly maxBytes?: number;
}

export const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;
const DEFAULT_MAX_BYTES = 1_048_576;

interface Envelope {
  /** Expiry, epoch ms. */
  readonly e: number;
  /** The value. */
  readonly v: unknown;
}

function resolveStorage(deps: TtlCacheDeps): StorageLike | null {
  if (deps.storage !== undefined) return deps.storage;
  try {
    return (globalThis as { localStorage?: StorageLike }).localStorage ?? null;
  } catch {
    return null;
  }
}

export function createTtlCache(deps: TtlCacheDeps = {}): TtlCache {
  const storage = resolveStorage(deps);
  const ttlMs = deps.ttlMs ?? DEFAULT_TTL_MS;
  const namespace = deps.namespace ?? 'sm.ttl.';
  const now = deps.now ?? (() => Date.now());
  const maxBytes = deps.maxBytes ?? DEFAULT_MAX_BYTES;

  /** Session fallback when storage is unavailable. */
  const memory = new Map<string, Envelope>();
  const k = (key: string): string => `${namespace}${key}`;

  function readEnvelope(key: string): Envelope | null {
    // Prefer storage, but fall back to the in-memory copy on a miss OR a throw —
    // a `set` whose `setItem` was rejected (quota / blocked) lives only there.
    if (storage !== null) {
      try {
        const raw = storage.getItem(k(key));
        if (raw !== null) {
          const parsed: unknown = JSON.parse(raw);
          if (
            typeof parsed === 'object' &&
            parsed !== null &&
            typeof (parsed as Envelope).e === 'number'
          ) {
            return parsed as Envelope;
          }
        }
      } catch {
        /* fall through to the memory copy */
      }
    }
    return memory.get(k(key)) ?? null;
  }

  return {
    get<T>(key: string): T | null {
      const env = readEnvelope(key);
      if (env === null) return null;
      if (now() >= env.e) {
        // Expired — evict so it cannot linger, then miss.
        this.delete(key);
        return null;
      }
      return env.v as T;
    },

    set(key, value) {
      const env: Envelope = { e: now() + ttlMs, v: value };
      if (storage === null) {
        memory.set(k(key), env);
        return;
      }
      let serialized: string;
      try {
        serialized = JSON.stringify(env);
      } catch {
        return; // non-serializable — skip the cache, never break the caller
      }
      if (serialized.length > maxBytes) return; // too big to be worth persisting
      try {
        storage.setItem(k(key), serialized);
      } catch {
        // Quota / blocked — keep it for the session at least.
        memory.set(k(key), env);
      }
    },

    delete(key) {
      memory.delete(k(key));
      if (storage === null) return;
      try {
        storage.removeItem(k(key));
      } catch {
        /* ignore */
      }
    },
  };
}
